import type { Node } from '@atlaskit/editor-prosemirror/model';

import { fg } from '@confluence/feature-gating';

import { getEstimatedHeight as getUpdatedEstimatedHeight } from '../../utils/node-estimated-height.ts';
import type { ChildInfoOutput } from '../../utils/node-estimated-height.ts';

const NODES_WITH_NO_CHILDREN = {
	blockCard: { height: 114 },
	caption: { height: 32 },
	date: { height: 20 },
	emoji: { height: 28 },
	inlineCard: { height: 26 },
	mediaInline: { height: 28 },
	mention: { height: 24 },
	placeholder: { height: 28 },
	rule: { height: 30 },
	status: { height: 16 },
	// expand + nestedExpand have children, but they are collapsed by default. So, we treat them as having no children for height estimation.
	expand: { height: 40 },
	nestedExpand: { height: 40 },
};

const NODES_WITH_TEXT_CHILDREN = {
	decisionItem: { contentHeight: 36 },
	paragraph: { contentHeight: 28 },
	taskItem: { contentHeight: 28 },
};

const NODES_WITH_INLINE_CHILDREN = {
	layoutSection: { additionalHeight: 44, minimumHeight: 72 },
	tableRow: { additionalHeight: 0, minimumHeight: 48 },
};

const NODES_WITH_BLOCK_CHILDREN = {
	blockquote: { additionalHeight: 20, minimumHeight: 48 },
	bulletList: { additionalHeight: 0, minimumHeight: 0 },
	decisionList: { additionalHeight: 0, minimumHeight: 0 },
	layoutColumn: { additionalHeight: 0, minimumHeight: 28 },
	listItem: { additionalHeight: 0, minimumHeight: 28 },
	orderedList: { additionalHeight: 0, minimumHeight: 0 },
	panel: { additionalHeight: 20, minimumHeight: 48 },
	table: { additionalHeight: 60, minimumHeight: 108 },
	tableCell: { additionalHeight: 20, minimumHeight: 48 },
	tableHeader: { additionalHeight: 20, minimumHeight: 48 },
	taskList: { additionalHeight: 0, minimumHeight: 0 },
};

const MEDIA_SINGLE_MINIMUM_WIDTH = 620;

// Calculates the estimated height of a node based on its type.
// We underestimate the heights to avoid rendering too few nodes.
export const getEstimatedHeight = (node: Node, nodeChildInfoOutput: ChildInfoOutput): number => {
	// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
	if (fg('platform_renderer_shared_height_estimator')) {
		return getUpdatedEstimatedHeight(node, nodeChildInfoOutput) ?? 0;
	}

	const nodeTypeName = node?.type?.name || '';
	if (NODES_WITH_NO_CHILDREN[nodeTypeName]) {
		return NODES_WITH_NO_CHILDREN[nodeTypeName].height || 0;
	} else if (NODES_WITH_INLINE_CHILDREN[nodeTypeName]) {
		const additionalHeight = NODES_WITH_INLINE_CHILDREN[nodeTypeName].additionalHeight || 0;
		const minimumHeight = NODES_WITH_INLINE_CHILDREN[nodeTypeName].minimumHeight || 0;
		const childrenHeight = Math.max(
			...nodeChildInfoOutput.estimatedChildHeights.map((height) => height || 0),
			// 0 is added in case there are no entries in estimatedChildHeights.
			0,
		);
		return Math.max(childrenHeight + additionalHeight, minimumHeight);
	} else if (NODES_WITH_BLOCK_CHILDREN[nodeTypeName]) {
		const additionalHeight = NODES_WITH_BLOCK_CHILDREN[nodeTypeName].additionalHeight || 0;
		const minimumHeight = NODES_WITH_BLOCK_CHILDREN[nodeTypeName].minimumHeight || 0;
		const childrenHeight = nodeChildInfoOutput.estimatedChildHeights.reduce(
			(acc, height) => acc + (height || 0),
			0,
		);
		return Math.max(childrenHeight + additionalHeight, minimumHeight);
	} else if (NODES_WITH_TEXT_CHILDREN[nodeTypeName]) {
		const contentHeight = NODES_WITH_TEXT_CHILDREN[nodeTypeName].contentHeight || 0;
		const lineCount =
			node.content?.content?.reduce(
				(acc, child) => (child?.type?.name === 'hardBreak' ? acc + 1 : acc),
				1,
			) || 1;
		return lineCount * contentHeight;
	} else if (nodeTypeName === 'heading') {
		const HEADING_HEIGHTS = {
			1: 32,
			2: 28,
			3: 28,
			4: 24,
			5: 18,
			6: 18,
		};
		const contentHeight = HEADING_HEIGHTS[node?.attrs?.level] || 0;
		const lineCount =
			node.content?.content?.reduce(
				(acc, child) => (child?.type?.name === 'hardBreak' ? acc + 1 : acc),
				1,
			) || 1;
		return lineCount * contentHeight;
	} else if (
		nodeTypeName === 'mediaSingle' &&
		['align-start', 'center', 'align-end', 'wide', 'full-width'].includes(node?.attrs?.layout)
	) {
		const mediaSingleAttrsWidth = node?.attrs?.width;
		const media = node?.content?.[0]; // 'media' node type
		const mediaAttrsWidth = media?.attrs?.width;
		const mediaAttrsHeight = media?.attrs?.height;
		if (!mediaSingleAttrsWidth || !mediaAttrsWidth || !mediaAttrsHeight) {
			return 0; // If media attributes are not available, return 0
		}

		const minMediaWidth = Math.min(mediaSingleAttrsWidth, MEDIA_SINGLE_MINIMUM_WIDTH);
		const minMediaHeight = (minMediaWidth / mediaAttrsWidth) * mediaAttrsHeight;

		const caption = node?.content?.[1]; // 'caption' node type
		const captionHeight = caption ? 32 : 0;

		return minMediaHeight + captionHeight;
	} else if (nodeTypeName === 'mediaGroup') {
		// mediaGroup is similar to mediaSingle, but it can contain multiple media nodes.
		// Unfortunately, testing this case has proven difficult, so we return 0 for now.
		// This may be improved in the future.
		return 0;
	} else if (
		nodeTypeName === 'embedCard' &&
		['align-start', 'center', 'align-end', 'wide', 'full-width'].includes(node?.attrs?.layout)
	) {
		// The height of embedCards is likely much larger than 58, but it depends on a number of factors.
		// This is used as a safe simple value which may be improved upon in the future.
		return 58;
	} else if (nodeTypeName === 'codeBlock') {
		const additionalHeight = 16;
		const minimumHeight = 40;

		// Text takes up at least 24px per line, so we multiply the number of lines by 24.
		const contentHeight = (node?.content?.content?.length || 0) * 24;
		return Math.max(contentHeight + additionalHeight, minimumHeight);
	} else if (
		[
			'bodiedExtension',
			'extension',
			'extensionFrame',
			'inlineExtension',
			'multiBodiedExtension',
		].includes(nodeTypeName)
	) {
		// Extensions likely take up significant height, but it depends on the content. For now, we return 0 to avoid
		// overestimating their height, but this may be improved in the future.
		return 0;
	} else {
		return 0; // Default case for unrecognized node types
	}
};
